/**
 * ScreenCorrection - Tela de Correção de Dados
 * Exibida quando o admin solicita correção de dados do motorista
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertTriangle, Edit3, ArrowRight, LogOut } from 'lucide-react';

export function ScreenCorrection() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [driverName, setDriverName] = useState<string>('');
    const [correctionMessage, setCorrectionMessage] = useState<string>('');

    useEffect(() => {
        async function checkStatus() {
            try {
                const { data: { user }, error: authError } = await supabase.auth.getUser();

                if (authError || !user) {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                // Verificar o status do perfil
                const { data: driverData, error: driverError } = await supabase
                    .from('drivers')
                    .select('nome_completo, profile_status, correction_message')
                    .eq('id', user.id)
                    .single();

                if (driverError) {
                    console.error('[ScreenCorrection] Erro ao buscar driver:', driverError);
                    navigate('/driver/login', { replace: true });
                    return;
                }

                setDriverName(driverData.nome_completo || 'Motorista');
                setCorrectionMessage(driverData.correction_message || '');

                // Redirecionar baseado no status
                if (driverData.profile_status === 'approved') {
                    navigate('/dash_companion', { replace: true });
                    return;
                }

                if (driverData.profile_status === 'pending_submission') {
                    navigate('/driver/onboarding', { replace: true });
                    return;
                }

                if (driverData.profile_status === 'pending_review') {
                    navigate('/driver/review', { replace: true });
                    return;
                }

                if (driverData.profile_status === 'rejected') {
                    navigate('/driver/rejected', { replace: true });
                    return;
                }

                setIsLoading(false);
            } catch (error) {
                console.error('[ScreenCorrection] Erro:', error);
                navigate('/driver/login', { replace: true });
            }
        }

        checkStatus();
    }, [navigate]);

    const handleGoToOnboarding = () => {
        navigate('/driver/onboarding');
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/driver/login', { replace: true });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-700" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex flex-col items-center justify-center p-4">
            {/* Container Principal */}
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img
                        src="https://locaninja.com/logotipo-watch.png"
                        alt="Watch Logo"
                        className="h-12 mx-auto mb-4"
                    />
                </div>

                {/* Card Principal */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Header do Card */}
                    <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-6">
                        <div className="flex items-center justify-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <AlertTriangle className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <h1 className="text-xl font-bold text-white text-center mt-4">
                            Correção Necessária
                        </h1>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="p-6">
                        {/* Saudação */}
                        <p className="text-gray-700 text-center mb-6">
                            Olá, <span className="font-semibold text-gray-900">{driverName}</span>
                        </p>

                        {/* Mensagem Principal */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                            <p className="text-emerald-900 text-sm leading-relaxed text-center mb-3">
                                Nossa equipe analisou seu cadastro e identificou que algumas informações precisam ser corrigidas para prosseguirmos com sua aprovação.
                            </p>
                        </div>

                        {/* Mensagem de Correção do Admin */}
                        {correctionMessage && (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1">
                                    <Edit3 className="w-3 h-3" />
                                    Detalhes da correção
                                </p>
                                <p className="text-gray-800 text-sm leading-relaxed">
                                    {correctionMessage}
                                </p>
                            </div>
                        )}

                        {/* Instruções */}
                        <div className="space-y-3 mb-6">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-emerald-700 text-xs font-bold">1</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Clique no botão abaixo para acessar o formulário
                                </p>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-emerald-700 text-xs font-bold">2</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Corrija as informações conforme indicado acima
                                </p>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-emerald-700 text-xs font-bold">3</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Envie novamente para análise
                                </p>
                            </div>
                        </div>

                        {/* Botão de Ação */}
                        <button
                            onClick={handleGoToOnboarding}
                            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-700 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-green-800 transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
                        >
                            Corrigir Cadastro
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Footer do Card */}
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                        <button
                            onClick={handleLogout}
                            className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors flex items-center justify-center gap-2"
                        >
                            <LogOut className="w-4 h-4" />
                            Sair da conta
                        </button>
                    </div>
                </div>

                {/* Texto de Rodapé */}
                <p className="text-center text-gray-400 text-xs mt-6">
                    Após a correção, seu cadastro será analisado novamente.
                </p>
            </div>
        </div>
    );
}

export default ScreenCorrection;
